// circular.js
import { useState, useEffect } from "react";
import {  Megaphone as MegaphoneIcon, Users, UserCheck, X, ChevronDown, ChevronUp, Mail, Lightbulb, Save, Pencil, Trash2, Inbox, XCircle, CheckCircle2, } from "lucide-react";

// ─── DATA ──────────────────────────────────────────────────────────────────────
const allClasses = ["UKG", "LKG", "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7", "Class 8", "Class 9", "Class 10", "Class 11", "Class 12"];

// ─── UTILITY COMPONENTS ───────────────────────────────────────────────────────

function GlassCard({
  children,
  className = "",
  hover = true
}) {
  return <div className={`glass-panel rounded-2xl shadow-lg shadow-slate-200/50 ${hover ? 'card-hover' : ''} ${className}`}>
      {children}
    </div>;
}
function StatusBadge({
  status
}) {
  const map = {
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    review: "bg-blue-50 text-blue-700 border-blue-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partial: "bg-amber-50 text-amber-700 border-amber-200",
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    draft: "bg-amber-50 text-amber-700 border-amber-200",
    pending_send: "bg-blue-50 text-blue-700 border-blue-200"
  };
  const labels = {
    sent: "Sent",
    draft: "Draft",
    pending_send: "Pending"
  };
  return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider border shadow-sm ${map[status] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
      {labels[status] || status}
    </span>;
}

// ─── RECIPIENT SELECTION MODAL ──────────────────────────────────────────────

function RecipientSelectionModal({
  isOpen,
  onClose,
  selectedClass,
  onSend,
  loading
}) {
  const [selectedType, setSelectedType] = useState(null); // 'parents' or 'staff'
  const [sections, setSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  useEffect(() => {
    if (selectedClass) {
      // For Class 10, show sections A, B, C
      // For other classes, you can customize this logic
      const classSections = ['A', 'B', 'C'];
      // You could also check if the class is 11 or 12 to show specific sections
      setSections(classSections);
      setSelectedSections([]);
      setSelectedType(null);
    }
  }, [selectedClass]);
  if (!isOpen) return null;
  const toggleSection = section => {
    setSelectedSections(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
  };
  const handleSend = () => {
    if (!selectedType || selectedSections.length === 0) return;
    onSend({
      type: selectedType,
      sections: selectedSections,
      class: selectedClass
    });
  };
  return <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[200] animate-fade-in-scale" onClick={onClose}>
      <div className="bg-white rounded-3xl w-[500px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-fade-in-scale" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-950 via-purple-900 to-violet-950 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-white font-bold text-lg flex items-center gap-2">
              <span className="text-2xl"><Mail className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              <span>Send to {selectedClass}</span>
            </div>
            <div className="text-violet-300 text-xs mt-1">Select recipients and sections</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-all duration-200">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {/* Recipient Type Selection */}
          <div className="mb-6">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-3">
              Select Recipient Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setSelectedType('parents')} className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${selectedType === 'parents' ? 'border-violet-500 bg-violet-50 shadow-md' : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/50'}`}>
                <Users size={24} className={selectedType === 'parents' ? 'text-violet-600' : 'text-slate-400'} />
                <span className={`text-sm font-bold ${selectedType === 'parents' ? 'text-violet-700' : 'text-slate-600'}`}>
                  Parents
                </span>
                <span className="text-[10px] text-slate-400">All parents in selected sections</span>
              </button>
              <button onClick={() => setSelectedType('staff')} className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${selectedType === 'staff' ? 'border-violet-500 bg-violet-50 shadow-md' : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/50'}`}>
                <UserCheck size={24} className={selectedType === 'staff' ? 'text-violet-600' : 'text-slate-400'} />
                <span className={`text-sm font-bold ${selectedType === 'staff' ? 'text-violet-700' : 'text-slate-600'}`}>
                  Staff
                </span>
                <span className="text-[10px] text-slate-400">All staff members</span>
              </button>
            </div>
          </div>

          {/* Section Selection */}
          {selectedType && <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-3">
                Select Sections
              </label>
              <div className="flex flex-wrap gap-3">
                {sections.map(section => <button key={section} onClick={() => toggleSection(section)} className={`px-5 py-2.5 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${selectedSections.includes(section) ? 'bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-200' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300 hover:bg-violet-50'}`}>
                    Section {section}
                  </button>)}
                <button onClick={() => setSelectedSections(sections)} className={`px-5 py-2.5 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${selectedSections.length === sections.length && sections.length > 0 ? 'bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-200' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300 hover:bg-violet-50'}`}>
                  All Sections
                </button>
              </div>
            </div>}

          {/* Summary */}
          {selectedType && selectedSections.length > 0 && <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="text-xs text-slate-500 font-medium">Summary</div>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Recipient Type:</span>
                  <span className="font-bold text-slate-800 capitalize">{selectedType}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Class:</span>
                  <span className="font-bold text-slate-800">{selectedClass}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Sections:</span>
                  <span className="font-bold text-slate-800">{selectedSections.join(', ')}</span>
                </div>
              </div>
            </div>}

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3">
            <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all duration-200">
              Cancel
            </button>
            <button onClick={handleSend} disabled={!selectedType || selectedSections.length === 0 || loading} className="flex-1 py-3 bg-gradient-to-r from-violet-600 to-purple-700 text-white font-bold rounded-xl shadow-lg shadow-violet-500/25 hover:from-violet-700 hover:to-purple-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Sending...
                </span> : 'Send Now'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}

// ─── CIRCULAR COMPONENT ──────────────────────────────────────────────────────

export default function Circular() {
  const API_BASE = process.env.REACT_APP_API_URL || 'https://schoolcrm-fzcv.onrender.com';
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [allParents, setAllParents] = useState(false);
  const [allStaff, setAllStaff] = useState(false);
  const [classesSel, setClassesSel] = useState([]);
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [notice, setNotice] = useState('');
  const [circTab, setCircTab] = useState('compose');

  // Modal states
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [selectedClassForModal, setSelectedClassForModal] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  useEffect(() => {
    fetchRecent();
  }, []);
  async function fetchRecent() {
    try {
      const res = await fetch(`${API_BASE}/api/circulars`);
      const j = await res.json();
      if (j.success) setRecent(j.circulars);
    } catch (err) {
      console.error(err);
    }
  }

  // Handle class selection - shows modal
  function handleClassSelect(cls) {
    setSelectedClassForModal(cls);
    setShowRecipientModal(true);
  }

  // Handle send from modal
  async function handleModalSend(recipientData) {
    setModalLoading(true);
    try {
      const {
        type,
        sections,
        class: className
      } = recipientData;

      // Build recipientsMeta
      const recipientsMeta = {
        allStaff: type === 'staff',
        allParents: type === 'parents',
        classes: [className]
      };
      const body = {
        title,
        message,
        recipientsMeta,
        isDraft: false,
        sections: sections,
        recipientType: type
      };
      let res, j;
      if (editingId) {
        res = await fetch(`${API_BASE}/api/circulars/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      } else {
        res = await fetch(`${API_BASE}/api/circulars`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      }
      if (j.success) {
        setNotice(`Circular sent to ${type} in ${className} (Sections: ${sections.join(', ')})`);
        setTitle('');
        setMessage('');
        setClassesSel([]);
        setAllParents(false);
        setAllStaff(false);
        setEditingId(null);
        setShowRecipientModal(false);
        fetchRecent();
      } else {
        setNotice("Send failed:" + (j.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      setNotice("Send failed:" + err.message);
    }
    setModalLoading(false);
  }
  async function handleSendNow() {
    setSending(true);
    setNotice('');
    try {
      // If specific classes are selected, show modal for first one
      if (classesSel.length === 1) {
        handleClassSelect(classesSel[0]);
        setSending(false);
        return;
      } else if (classesSel.length > 1) {
        // For multiple classes, use the traditional method
        const body = {
          title,
          message,
          recipientsMeta: {
            allParents,
            allStaff,
            classes: classesSel
          },
          isDraft: false
        };
        let res, j;
        if (editingId) {
          res = await fetch(`${API_BASE}/api/circulars/${editingId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });
          j = await res.json();
        } else {
          res = await fetch(`${API_BASE}/api/circulars`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });
          j = await res.json();
        }
        if (j.success) {
          setNotice(editingId ? "Circular updated & sent" : "Circular sent");
          setTitle('');
          setMessage('');
          setClassesSel([]);
          setAllParents(false);
          setAllStaff(false);
          setEditingId(null);
          fetchRecent();
        } else {
          setNotice("Send failed");
        }
        setSending(false);
        return;
      }

      // No specific classes - use traditional send
      const body = {
        title,
        message,
        recipientsMeta: {
          allParents,
          allStaff,
          classes: classesSel
        },
        isDraft: false
      };
      let res, j;
      if (editingId) {
        res = await fetch(`${API_BASE}/api/circulars/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      } else {
        res = await fetch(`${API_BASE}/api/circulars`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      }
      if (j.success) {
        setNotice(editingId ? "Circular updated & sent" : "Circular sent");
        setTitle('');
        setMessage('');
        setClassesSel([]);
        setAllParents(false);
        setAllStaff(false);
        setEditingId(null);
        fetchRecent();
      } else {
        setNotice("Send failed");
      }
    } catch (err) {
      console.error(err);
      setNotice("Send failed");
    }
    setSending(false);
  }
  function startEditCircular(id) {
    const c = recent.find(x => x._id === id);
    if (!c) return;
    setTitle(c.title || '');
    setMessage(c.message || '');
    setAllParents(!!c.recipientsMeta?.allParents);
    setAllStaff(!!c.recipientsMeta?.allStaff);
    setClassesSel(Array.isArray(c.recipientsMeta?.classes) ? c.recipientsMeta.classes : []);
    setEditingId(id);
    setNotice(" Editing circular");
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  async function deleteCircular(id) {
    if (!window.confirm('Delete this circular?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/circulars/${id}`, {
        method: 'DELETE'
      });
      const j = await res.json();
      if (j.success) {
        setNotice(" Deleted");
        fetchRecent();
      } else setNotice("Delete failed");
    } catch (err) {
      console.error(err);
      setNotice("Delete failed");
    }
  }
  function toggleClass(cls) {
    setClassesSel(prev => prev.includes(cls) ? prev.filter(x => x !== cls) : [...prev, cls]);
  }
  return <div className="animate-fade-in-up">
      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-slate-800 flex items-center gap-3">
          <span className="text-3xl"><MegaphoneIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Send Circular
          <span className="hidden md:inline text-sm text-slate-400 font-medium">/ Notices & Announcements</span>
        </h2>
      </div>

      {/* Recipient Selection Modal */}
      <RecipientSelectionModal isOpen={showRecipientModal} onClose={() => setShowRecipientModal(false)} selectedClass={selectedClassForModal} onSend={handleModalSend} loading={modalLoading} />

      <div className="md:hidden mb-4">
        <div className="flex gap-2 mb-3">
          <button onClick={() => setCircTab('compose')} className={`flex-1 text-sm font-bold px-4 py-2 rounded-xl ${circTab === 'compose' ? 'bg-violet-600 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}>
            Compose
          </button>
          <button onClick={() => setCircTab('recent')} className={`flex-1 text-sm font-bold px-4 py-2 rounded-xl ${circTab === 'recent' ? 'bg-violet-600 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}>
            Recent Circulars
          </button>
        </div>

        {circTab === 'compose' ? <GlassCard>
            <div className="p-7">
              <div className="mb-6">
                <div className="text-base font-bold text-slate-700">Compose Circular</div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="text-[11px] text-slate-500 font-bold uppercase tracking-wide block mb-2">Circular Title</label>
                  <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all shadow-sm" placeholder="Enter circular title..." />
                </div>

                <div>
                  <label className="text-[11px] text-slate-500 font-bold uppercase tracking-wide block mb-2">Send To</label>
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 transition-all">
                      <input type="checkbox" checked={allParents} onChange={e => setAllParents(e.target.checked)} className="accent-violet-600 w-4 h-4" />
                      <span className="font-medium">All Parents</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 transition-all">
                      <input type="checkbox" checked={allStaff} onChange={e => setAllStaff(e.target.checked)} className="accent-violet-600 w-4 h-4" />
                      <span className="font-medium">All Staff</span>
                    </label>
                  </div>
                  <div className="w-full text-[11px] text-slate-400 mt-4 font-medium">Or select classes:</div>
                  <div className="w-full mt-3 flex flex-wrap gap-2">
                    {allClasses.map(c => <button key={c} onClick={() => toggleClass(c)} className={`text-xs px-4 py-2 rounded-full border transition-all duration-200 font-bold shadow-sm ${classesSel.includes(c) ? 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                        {c}
                      </button>)}
                  </div>
                  {classesSel.length === 1 && <div className="mt-3 p-3 bg-violet-50 rounded-xl border border-violet-200">
                      <div className="text-xs text-violet-600 font-medium flex items-center gap-2">
                        <span><Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> 
                        Clicking "Send Now" will open a popup to select Parents or Staff
                      </div>
                    </div>}
                </div>

                <div>
                  <label className="text-[11px] text-slate-500 font-bold uppercase tracking-wide block mb-2">Message (Tamil / English)</label>
                  <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-violet-100 focus:border-violet-300 resize-none h-36 transition-all shadow-sm" placeholder="Type your circular here...&#10;&#10;உதாரணம்: Unit Test 2 April 22 முதல் 26 வரை நடைபெறும்." />
                </div>

                <div className="flex gap-3">
                  <button onClick={handleSendNow} disabled={sending} className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 text-white text-sm font-bold px-6 py-3 rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                    <span className="text-lg"><MegaphoneIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                    {sending ? <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        Sending...
                      </span> : "Send Now"}
                  </button>
                  <button onClick={() => {
                setNotice("Saved as draft (not persisted)");
              }} className="text-sm px-5 py-3 border border-slate-200 rounded-xl bg-white text-slate-600 font-bold hover:bg-slate-50 transition-all duration-200 shadow-sm">
                    <span className="mr-1"><Save className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Save Draft
                  </button>
                </div>
              </div>
            </div>
          </GlassCard> : <GlassCard>
            <div className="p-7">
              <div className="text-base font-bold text-slate-700 mb-5 flex items-center gap-2">
                <span className="text-lg"><Mail className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Recent Circulars
              </div>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 -mr-2">
                {recent.map(r => <div key={r._id} className={`bg-white border border-slate-100 rounded-xl p-5 relative shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 overflow-hidden`}>
                    <div className={`absolute inset-y-0 left-0 w-1.5 ${r.isDraft ? 'bg-amber-400' : 'bg-emerald-400'}`}></div>
                    <div className="absolute top-4 right-4 flex gap-2">
                      <button onClick={() => startEditCircular(r._id)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-violet-600 bg-white hover:shadow-md transition-all duration-200 font-bold">
                        <Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Edit
                      </button>
                      <button onClick={() => deleteCircular(r._id)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-red-600 bg-white hover:shadow-md transition-all duration-200 font-bold">
                        <Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Delete
                      </button>
                    </div>
                    <div className="flex items-start gap-4 pr-24">
                      <div className="flex-1">
                        <div className="text-sm font-bold text-slate-800">{r.title}</div>
                        <div className="text-[12px] text-slate-400 mt-2">{new Date(r.createdAt).toLocaleString()}</div>
                        <div className="mt-3 text-[12px] text-slate-600 font-medium">
                          To: {r.recipientsMeta?.allParents ? "All Parents" : ""} {r.recipientsMeta?.allStaff ? " All Staff" : ""} {r.recipientsMeta?.classes?.length ? ` Classes: ${r.recipientsMeta.classes.join(', ')}` : ""}
                          {r.sections && r.sections.length > 0 && ` Sections: ${r.sections.join(', ')}`}
                          {r.recipientType && ` (${r.recipientType})`}
                        </div>
                        {r.message && <div className={`mt-4 text-[13px] whitespace-pre-line rounded-xl p-4 ${r.isDraft ? 'bg-amber-50 border border-amber-100 text-slate-700' : 'bg-slate-50 text-slate-700 border border-slate-100'}`}>
                            {r.message.length > 300 ? r.message.slice(0, 300) + '...' : r.message}
                          </div>}
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <StatusBadge status={r.isDraft ? 'draft' : 'sent'} />
                    </div>
                  </div>)}
                {recent.length === 0 && <div className="text-center py-12 text-slate-400">
                    <div className="text-4xl mb-3"><Inbox className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
                    <div className="text-sm font-medium">No circulars sent yet</div>
                  </div>}
              </div>
            </div>
          </GlassCard>}
      </div>

      <div className="hidden md:grid grid-cols-2 gap-6">
        <GlassCard>
          <div className="p-7">
            <div className="flex items-center justify-between mb-6">
              <div className="text-base font-bold text-slate-700">Compose Circular</div>
              <div className="text-xs text-slate-400 font-medium">Create a polished notice for parents & staff</div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="text-[11px] text-slate-500 font-bold uppercase tracking-wide block mb-2">Circular Title</label>
                <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all shadow-sm" placeholder="Enter circular title..." />
              </div>

              <div>
                <label className="text-[11px] text-slate-500 font-bold uppercase tracking-wide block mb-2">Send To</label>
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 transition-all">
                    <input type="checkbox" checked={allParents} onChange={e => setAllParents(e.target.checked)} className="accent-violet-600 w-4 h-4" />
                    <span className="font-medium">All Parents</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 transition-all">
                    <input type="checkbox" checked={allStaff} onChange={e => setAllStaff(e.target.checked)} className="accent-violet-600 w-4 h-4" />
                    <span className="font-medium">All Staff</span>
                  </label>
                </div>
                <div className="w-full text-[11px] text-slate-400 mt-4 font-medium">Or select classes:</div>
                <div className="w-full mt-3 flex flex-wrap gap-2">
                  {allClasses.map(c => <button key={c} onClick={() => toggleClass(c)} className={`text-xs px-4 py-2 rounded-full border transition-all duration-200 font-bold shadow-sm ${classesSel.includes(c) ? 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                      {c}
                    </button>)}
                </div>
                {classesSel.length === 1 && <div className="mt-3 p-3 bg-violet-50 rounded-xl border border-violet-200">
                    <div className="text-xs text-violet-600 font-medium flex items-center gap-2">
                      <span><Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> 
                      Clicking "Send Now" will open a popup to select Parents or Staff
                    </div>
                  </div>}
              </div>

              <div>
                <label className="text-[11px] text-slate-500 font-bold uppercase tracking-wide block mb-2">Message (Tamil / English)</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-violet-100 focus:border-violet-300 resize-none h-36 transition-all shadow-sm" placeholder="Type your circular here...&#10;&#10;உதாரணம்: Unit Test 2 April 22 முதல் 26 வரை நடைபெறும்." />
              </div>

              <div className="flex gap-3">
                <button onClick={handleSendNow} disabled={sending} className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 text-white text-sm font-bold px-6 py-3 rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                  <span className="text-lg"><MegaphoneIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                  {sending ? <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Sending...
                    </span> : "Send Now"}
                </button>
                <button onClick={() => {
                setNotice("Saved as draft (not persisted)");
              }} className="text-sm px-5 py-3 border border-slate-200 rounded-xl bg-white text-slate-600 font-bold hover:bg-slate-50 transition-all duration-200 shadow-sm">
                  <span className="mr-1"><Save className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Save Draft
                </button>
              </div>

              {notice && <div className={`rounded-xl px-5 py-3 text-sm font-bold animate-fade-in-up shadow-sm flex items-center gap-2 ${notice.includes(<XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />) || notice.includes('failed') ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
                  <span>{notice.includes(<XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />) || notice.includes('failed') ? "" : <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />}</span> {notice}
                </div>}
            </div>
          </div>
        </GlassCard>

        <GlassCard>
          <div className="p-7">
            <div className="text-base font-bold text-slate-700 mb-5 flex items-center gap-2">
              <span className="text-lg"><Mail className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Recent Circulars
            </div>
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 -mr-2">
              {recent.map(r => <div key={r._id} className={`bg-white border border-slate-100 rounded-xl p-5 relative shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 overflow-hidden`}>
                  <div className={`absolute inset-y-0 left-0 w-1.5 ${r.isDraft ? 'bg-amber-400' : 'bg-emerald-400'}`}></div>
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button onClick={() => startEditCircular(r._id)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-violet-600 bg-white hover:shadow-md transition-all duration-200 font-bold">
                      <Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Edit
                    </button>
                    <button onClick={() => deleteCircular(r._id)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-red-600 bg-white hover:shadow-md transition-all duration-200 font-bold">
                      <Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Delete
                    </button>
                  </div>
                  <div className="flex items-start gap-4 pr-24">
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-800">{r.title}</div>
                      <div className="text-[12px] text-slate-400 mt-2">{new Date(r.createdAt).toLocaleString()}</div>
                      <div className="mt-3 text-[12px] text-slate-600 font-medium">
                        To: {r.recipientsMeta?.allParents ? "All Parents" : ""} {r.recipientsMeta?.allStaff ? " All Staff" : ""} {r.recipientsMeta?.classes?.length ? ` Classes: ${r.recipientsMeta.classes.join(', ')}` : ""}
                        {r.sections && r.sections.length > 0 && ` Sections: ${r.sections.join(', ')}`}
                        {r.recipientType && ` (${r.recipientType})`}
                      </div>
                      {r.message && <div className={`mt-4 text-[13px] whitespace-pre-line rounded-xl p-4 ${r.isDraft ? 'bg-amber-50 border border-amber-100 text-slate-700' : 'bg-slate-50 text-slate-700 border border-slate-100'}`}>
                          {r.message.length > 300 ? r.message.slice(0, 300) + '...' : r.message}
                        </div>}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <StatusBadge status={r.isDraft ? 'draft' : 'sent'} />
                  </div>
                </div>)}
              {recent.length === 0 && <div className="text-center py-12 text-slate-400">
                  <div className="text-4xl mb-3"><Inbox className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
                  <div className="text-sm font-medium">No circulars sent yet</div>
                </div>}
            </div>
          </div>
        </GlassCard>
      </div>
    </div>;
}